import { Sparkles, Info } from "lucide-react";
import { cn } from "@/lib/cn";
import { useCustomerMetrics, buildCustomerProse } from "./metrics";

interface Props {
  custno: string;
  locationCount: number;
  /** 360-B name card: a bare line under the name, no tinted box. */
  plain?: boolean;
}

/**
 * One-line operational summary of the customer, assembled from AI-ML-01
 * structured metrics. Sits right under the customer name in the header.
 *
 * Examples (real spot-check data 2026-05-25, after the statistical-vs-stated
 * vocabulary fix and the chimera-no-inherited-numbers rule):
 *   "24-year customer · 34 dispatches · $22K lifetime"
 *   "23-year property manager · 90 properties · 658 dispatches · $1.8M lifetime"
 *   "Recent buyer at this property"   (chimera variant — no counts, no revenue)
 *
 * Renders nothing while metrics load (no layout flash) and nothing when
 * there's no story to tell yet (brand-new customer with zero history).
 *
 * Chimera variant gets a soft amber tint so the dispatcher has a visual
 * cue without being alarmed. INTERIM treatment per BUILD-13a v1 — when
 * BUILD-17 ships DEC-NEW-04's ownership timeline, the variant retires and
 * the chimera customer gets their true (current-tenure-attributed) counts.
 */
export function CustomerProseSummary({ custno, locationCount, plain }: Props) {
  const { data: metrics, isLoading: metricsLoading } =
    useCustomerMetrics(custno);

  if (metricsLoading) return null;

  const prose = buildCustomerProse({
    metrics: metrics ?? null,
    locationCount,
  });
  if (!prose) return null;

  const Icon = prose.isChimera ? Info : Sparkles;

  return (
    <div
      className={cn(
        "flex items-start gap-2 text-sm leading-relaxed",
        plain
          // NAV-360-04: no own margin — the band's story line owns spacing.
          ? cn(prose.isChimera ? "text-amber-900" : "text-slate-600")
          : cn("rounded-md px-3 py-2", prose.isChimera
            ? "bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-200/70"
            : "bg-brand-50/40 text-slate-700 ring-1 ring-inset ring-brand-100/60"),
      )}
    >
      <Icon
        className={cn(
          "mt-0.5 h-4 w-4 flex-shrink-0",
          prose.isChimera ? "text-amber-600" : "text-brand-500",
        )}
      />
      <p>{prose.text}</p>
    </div>
  );
}
